import type { PipeTransform } from '@angular/core';
import { Pipe } from '@angular/core';
import type { OnyxMaybeArray, OnyxOptional } from '@onyx/angular';
import { castArray, compact, isString } from 'lodash';
import type { PickDeep } from 'type-fest';
import type { Fleet } from '../../dashboard/fleet/common/interfaces/fleet';

const REGISTRATION_NUMBER_REGEXP = new RegExp(
  /\b([A-Z]{2,3})([1-9][0-9]{0,4}|[0-9]{4}[A-Z]|[1-9][0-9]{2}[A-Z]{2}|[1-9][A-Z][1-9][0-9]{2}|[1-9][A-Z]{2}[0-9]{2}|[A-Z][1-9][0-9]{2}|[0-9]{2}[A-Z]{2}|[1-9][A-Z][0-9]{2}|[0-9]{2}[A-Z][1-9]|[1-9][A-Z]{2}[1-9]|[A-Z]{2}[0-9]{2}|[1-9][0-9]{2}[A-Z]{2}|[A-Z][0-9]{2}[A-Z]|[A-Z][1-9][A-Z]{2}|[0-9]{5}|[0-9]{3}[A-Z]{3}|[0-9]{2}[A-Z]{3}|[A-Z]{5}[0-9]{3})\b/,
);

type FleetIdentifierData =
  | PickDeep<
      Fleet,
      'generalInformation.sideNumber' | 'generalInformation.registrationNumber'
    >
  | Pick<Fleet['generalInformation'], 'sideNumber' | 'registrationNumber'>
  | string;

type FleetIdentifierFormat = 'dot' | 'bracket' | 'short' | 'registrationNumber';

@Pipe({
  name: 'fleetIdentifier',
})
export class FleetIdentifierPipe implements PipeTransform {
  public transform(
    identifier: OnyxOptional<OnyxMaybeArray<OnyxOptional<FleetIdentifierData>>>,
    format: FleetIdentifierFormat = 'dot',
  ): string {
    const identifiers = compact(castArray(identifier));
    if (!identifiers.length) return '-';

    return identifiers
      .map((identifier) => this.format(identifier, format))
      .join(' / ');
  }

  private format(
    identifier: FleetIdentifierData,
    format: FleetIdentifierFormat,
  ): string {
    const data = !isString(identifier)
      ? 'generalInformation' in identifier
        ? identifier.generalInformation
        : identifier
      : identifier;
    const sideNumber = isString(data) ? null : data.sideNumber;
    let registrationNumber = isString(data) ? data : data.registrationNumber;

    const match = registrationNumber
      .replace(/\s+/g, '')
      .toUpperCase()
      .match(REGISTRATION_NUMBER_REGEXP);
    if (match) registrationNumber = `${match[1]} ${match[2]}`;

    if (!sideNumber) return registrationNumber;

    return {
      dot: `${registrationNumber} · ${sideNumber}`,
      bracket: `${registrationNumber} (${sideNumber})`,
      short: sideNumber || registrationNumber,
      registrationNumber: registrationNumber,
    }[format];
  }
}
